import { createSelector } from '@reduxjs/toolkit'
import { Mesh } from '@babylonjs/core'
import { ConstructionTab } from '../../../reducers/construction'
import { validate as isValidUUID } from 'uuid'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SceneManager } from '../../../visualization/sceneManager'
import {
  isDoorMesh,
  isWallMesh,
  isWindowMesh,
} from '../../../visualization/utils'
import BoxMeasurement from './Components/BoxMeasurement'
import {
  useEditWallTool,
  WallProperty,
} from '../../../visualization/tools/useEditWallTool'
import {
  useEditWindowTool,
  WindowProperty,
} from '../../../visualization/tools/useEditWindowTool'
import {
  DoorProperty,
  useEditDoorTool,
} from '../../../visualization/tools/useEditDoorTool'
import { BuildingEditingTool } from '../../../models/Construction'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { getBimObjectRefId } from '../../../utils/BimUtil'
import { isUUID } from '../../../utils/Common'

interface ElementMeasurementsBaseProps {
  isVisible: boolean
  isEditable: boolean
  mesh: Mesh
}

const selectIsNonEditableBimObject = createSelector(
  (state: RootState) => state.construction.selectedElementsIds,
  (state: RootState) => state.construction.convertData.building,
  (state: RootState) => state.bim.objects,
  (selectedElementsIds, building, bimObjects) => {
    let isNonEditableBimObject = false
    const selectedElementId = selectedElementsIds[0]
    if (selectedElementId) {
      const bimRefMapping = getBimObjectRefId(building, selectedElementId)
      if (
        bimRefMapping &&
        bimRefMapping.length &&
        bimRefMapping[0].bimobject_ref &&
        isUUID(bimRefMapping[0].bimobject_ref)
      ) {
        const bimObject = bimObjects.find(
          (bimObject) => bimObject.id === bimRefMapping[0].bimobject_ref,
        )
        if (!!bimObject) {
          isNonEditableBimObject = bimObject.isNonEditable ?? false
        }
      }
    }

    return isNonEditableBimObject
  },
)

const WallMeasurements = ({
  mesh,
  isEditable,
  isVisible,
}: ElementMeasurementsBaseProps) => {
  const { changeWallLength, changeWallThickness, saveChanges, resetChanges } =
    useEditWallTool(mesh)

  return (
    <>
      <BoxMeasurement
        mesh={mesh}
        dimension='x'
        isEditable={isEditable}
        isVisible={isVisible}
        onChange={(value) => changeWallLength(value)}
        onConfirm={() => saveChanges(WallProperty.Length)}
        onDismiss={() => resetChanges(WallProperty.Length)}
      />
      <BoxMeasurement mesh={mesh} dimension='y' isVisible={isVisible} />
      <BoxMeasurement
        mesh={mesh}
        dimension='z'
        isEditable={isEditable}
        isVisible={isVisible}
        onChange={(value) => changeWallThickness(value)}
        onConfirm={() => saveChanges(WallProperty.Thickness)}
        onDismiss={() => resetChanges(WallProperty.Thickness)}
      />
    </>
  )
}

const DoorMeasurements = ({
  mesh,
  isEditable,
  isVisible,
}: ElementMeasurementsBaseProps) => {
  const { changeDoorWidth, changeDoorHeight, saveChanges, resetChanges } =
    useEditDoorTool(mesh)

  return (
    <>
      <BoxMeasurement
        mesh={mesh}
        dimension='x'
        isVisible={isVisible}
        isEditable={isEditable}
        onChange={(value) => changeDoorWidth(value)}
        onConfirm={() => saveChanges(DoorProperty.Width)}
        onDismiss={() => resetChanges(DoorProperty.Width)}
      />
      <BoxMeasurement
        mesh={mesh}
        dimension='y'
        isVisible={isVisible}
        isEditable={isEditable}
        onChange={(value) => changeDoorHeight(value)}
        onConfirm={() => saveChanges(DoorProperty.Height)}
        onDismiss={() => resetChanges(DoorProperty.Height)}
      />
    </>
  )
}

const WindowMeasurements = ({
  mesh,
  isEditable,
  isVisible,
}: ElementMeasurementsBaseProps) => {
  const { changeWindowWidth, changeWindowHeight, saveChanges, resetChanges } =
    useEditWindowTool(mesh)

  return (
    <>
      <BoxMeasurement
        mesh={mesh}
        dimension='x'
        isVisible={isVisible}
        isEditable={isEditable}
        onChange={(value) => changeWindowWidth(value)}
        onConfirm={() => saveChanges(WindowProperty.Width)}
        onDismiss={() => resetChanges(WindowProperty.Width)}
      />
      <BoxMeasurement
        mesh={mesh}
        dimension='y'
        isVisible={isVisible}
        isEditable={isEditable}
        onChange={(value) => changeWindowHeight(value)}
        onConfirm={() => saveChanges(WindowProperty.Height)}
        onDismiss={() => resetChanges(WindowProperty.Height)}
      />
    </>
  )
}

const selectSelectedElementId = createSelector(
  [
    (state: RootState) => state.construction.selectedConstructionTab,
    (state: RootState) => state.construction.selectedTool,
    (state: RootState) => state.construction.selectedElementsIds,
  ],
  (constructionTab, tool, elementsIds) => {
    const elementId = elementsIds[0]
    if (
      constructionTab !== ConstructionTab.Editor ||
      tool !== BuildingEditingTool.Measure ||
      !elementId ||
      !isValidUUID(elementId)
    ) {
      return null
    }

    return elementId
  },
)

const BuildingElementMeasurements = ({ isVisible }: { isVisible: boolean }) => {
  const isNonEditableBimObject = useSelector(selectIsNonEditableBimObject)

  const [isSelectedMeshVisible, setIsSelectedMeshVisible] =
    useState<boolean>(true)
  const visibilityRef = useRef<boolean>(true)

  const selectedElementId = useSelector(selectSelectedElementId)
  const selectedMesh = useMemo(() => {
    if (!selectedElementId) {
      return null
    }

    const scene = SceneManager.getInstance().getScene()
    const mesh = scene?.getMeshById(selectedElementId)
    const allowedMeshTypes = [isWallMesh, isWindowMesh, isDoorMesh]

    if (
      mesh &&
      mesh instanceof Mesh &&
      allowedMeshTypes.some((fn) => fn(mesh))
    ) {
      return mesh
    }

    return null
  }, [selectedElementId])

  useEffect(() => {
    const scene = SceneManager.getInstance().getScene()
    if (!selectedMesh || !scene) {
      return
    }

    const observer = scene.onBeforeRenderObservable.add(() => {
      const isVisible = selectedMesh.isVisible
      if (isVisible !== visibilityRef.current) {
        setIsSelectedMeshVisible(isVisible)
        visibilityRef.current = isVisible
      }
    })

    return () => {
      observer.remove()
    }
  }, [selectedMesh])

  if (!selectedMesh) {
    return null
  }

  return (
    <>
      {isWallMesh(selectedMesh) && (
        <WallMeasurements
          mesh={selectedMesh}
          isVisible={isVisible && isSelectedMeshVisible}
          isEditable={!isNonEditableBimObject}
        />
      )}

      {isDoorMesh(selectedMesh) && (
        <DoorMeasurements
          mesh={selectedMesh}
          isVisible={isVisible && isSelectedMeshVisible}
          isEditable={!isNonEditableBimObject}
        />
      )}

      {isWindowMesh(selectedMesh) && (
        <WindowMeasurements
          mesh={selectedMesh}
          isVisible={isVisible && isSelectedMeshVisible}
          isEditable={!isNonEditableBimObject}
        />
      )}
    </>
  )
}

export default BuildingElementMeasurements
